<template>
  <div id="content">
    <div class="header col-md-12">
      <div class="header-inner row align-items-center">
        <div class="col-md-4 left">
          <div class="title">
            {{ $t('sidebar.pos') }} > {{ $t('sidebar.poscashier') }}
          </div>
        </div>
        <div class="col-md-8 right">
          <div class="action">
            <base-dropdown-filter-download
              :items="DOWNLOAD_EXTS"
              :loading="false"
              @onSelect="handleSelectDownload"
            />
            <base-filter-data-type
              :type="dataType"
              :items="REPORT_DATA_TYPES"
              @onSelect="handleSelectDataType"
            />
            <base-dropdown-filter-date-range
              :type="dateType"
              :range="{ start: dateStart, end: dateEnd }"
              @onSubmit="handleSubmitFilter"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="content col-md-12">

      <p v-if="!ready">{{ $t('message.loading') }}</p>

      <div
        v-else
        class="col-md-12 no-padding mt-2">
        <template v-if="posCashiers.length">
          <section class="mt-2 mb-4">
            <div>
              <section-title
                :title="datePeriod(dateStart, dateEnd)"
                fontweight="bold"/>
            </div>
          </section>
          <keep-alive>
            <report-cashier-table
              v-if="dataType === REPORT_DATA_TYPE_VALUE.TABLE"
              :items="posCashiers"
            />
            <report-cashier-chart
              v-if="dataType === REPORT_DATA_TYPE_VALUE.CHART_BAR"
              :items="posCashiers"
              :selectedLabels.sync="chartSelectedLabels"
              type="bar"
            />
            <report-cashier-chart
              v-if="dataType === REPORT_DATA_TYPE_VALUE.CHART_LINE"
              :items="posCashiers"
              :selectedLabels.sync="chartSelectedLabels"
              type="line"
            />
          </keep-alive>
        </template>
        <template v-else>
          {{ $t('message.nodatafound') }}
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import { FILTER_DATE_TYPE, DOWNLOAD_EXTS, DOWNLOAD_EXT_VALUE, REPORT_DATA_TYPES, REPORT_DATA_TYPE_VALUE } from '../../../../helpers/enum'
import { getDateStart, getDateEnd, getLastSevenDay } from '../../../../helpers'
import sidebarModule from './../../sidebarModule'
import XLSX from 'xlsx'
import { getFileNamePosCashier } from '@/helpers/exportFormater'
import userPermissionMixin from '@/mixins/userPermission'

const LAST_SEVEN_DAY = getLastSevenDay()

export default {
  mixins: [userPermissionMixin],

  data () {
    return {
      REPORT_DATA_TYPES,
      REPORT_DATA_TYPE_VALUE,
      DOWNLOAD_EXTS,
      ready: false,
      dateType: FILTER_DATE_TYPE.DAY,
      dateStart: LAST_SEVEN_DAY.START,
      dateEnd: LAST_SEVEN_DAY.END,
      dataType: REPORT_DATA_TYPE_VALUE.TABLE,
      chartSelectedLabels: {},
    }
  },

  computed: {
    ...mapGetters({
      dateformat: 'getDateFormat',
    }),
    posCashiers () {
      return this.$store.state.report.posCashiers.map((item) => {
        return {
          ...item,
          date: (() => {
            if (this.dateType === FILTER_DATE_TYPE.DAY) {
              return this.getMyLocalDate(item.date, { short: false })
            } else if (this.dateType === FILTER_DATE_TYPE.MONTH) {
              return this.getMyLocalMonthYear(item.date, { short: false })
            } else if (this.dateType === FILTER_DATE_TYPE.YEAR) {
              return this.getMyLocalYear(item.date, { short: false })
            }
            return item.date
          })(),
        }
      })
    },
  },

  beforeMount () {
    // init page
    this.initPage({
      section: 'app',
      name: this.$t('app.reports.title'),
      current: {
        app_name: 'reports',
        app_id: 8,
        module_id: 2,
        submodule_id: 2,
      },
    })
    // set sidebar ui
    sidebarModule.init(this.getObject(require('../sidebar.json')), {
      sidebarAtIdx: 1,
      submenuAtIdx: 1,
    })
  },

  created () {
    this.fetchData()
  },

  destroyed: function () {
    this.leavePage(this.$Progress, [])
  },

  methods: {
    async fetchData () {
      this.ready = false
      await this.$store.dispatch('getPosCashiers', {
        'filter[date_start]': getDateStart(this.dateStart),
        'filter[date_end]': getDateEnd(this.dateEnd),
        'filter[date_type]': this.dateType,
        // 'filter[cashier_ids]': '5e48bbf174e2e91e0f27b4de, 5fa174a86ffc162e5824b4f1, 5fa4badfd3173e7a8b07f40f',
        'timezone': moment.tz.guess(),
      })
      this.ready = true
      this.showFinish(this.$Progress)
    },
    handleSubmitFilter ({ type, start, end }) {
      this.dateType = type
      this.dateStart = start
      this.dateEnd = end
      this.fetchData()
    },
    handleSelectDataType ({ value = null }) {
      this.dataType = value
    },
    handleSelectDownload ({ value = null }) {
      if (value === DOWNLOAD_EXT_VALUE.EXCEL) {
        this.exportAs('xlsx')
      } else if (value === DOWNLOAD_EXT_VALUE.CSV) {
        this.exportAs('csv')
      }
    },
    async exportAs (fileType) {
      /* convert state to workbook */
      const headColumns = [
        this.$t('message.date'),
        this.$t('message.cashier'),
        this.$t('apps.pos.message.possession'),
        this.$t('message.transactions'),
        this.$t('message.product'),
        this.$t('message.new_customers'),
        this.$t('message.old_customers'),
        this.getRoleMetadata.view.fields.net ? this.$t('message.net') : '{hiden}',
        this.getRoleMetadata.view.fields.profit ? this.$t('message.profit') : '{hiden}',
      ].filter((v) => v !== '{hiden}')
      const bodyRows = this.$store.state.report.posCashiers.reduce((acc, cur) => {
        const results = cur.cashiers.map((cashierItem) => (
          [
            cur.date,
            cashierItem.name,
            cashierItem.sessionsCount,
            cashierItem.transactionsCount,
            cashierItem.productsCount,
            cashierItem.newCustomersCount,
            cashierItem.oldCustomersCount,
            this.getRoleMetadata.view.fields.net ? cashierItem.net : '{hiden}',
            this.getRoleMetadata.view.fields.profit ? cashierItem.profit : '{hiden}',
          ].filter((v) => v !== '{hiden}')
        ))
        acc = [
          ...acc,
          ...results,
        ]
        return acc
      }, [])
      const data = [headColumns, ...bodyRows]
      const ws = XLSX.utils.aoa_to_sheet(data)
      const wb = XLSX.utils.book_new()

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet')

      /* generate file and send to client */
      const fileName = getFileNamePosCashier(this.$t, fileType)

      XLSX.writeFile(wb, fileName)
    },
  },
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/apps/report/pos/Cashier.vue